/* VSKLO PDP gallery base controller.
 *
 * Initial-load owner for:
 * - Shopify product media + variant image grouping
 * - gallery navigation and mobile swipe
 * - native/external video activation
 *
 * Full-screen lightbox / zoom / pinch / pan are interaction-loaded from
 * assets/vsklo-pdp-gallery-lightbox.js.
 */
(function () {
  'use strict';

  if (window.__VSKLO_PDP_GALLERY_OWNER_V4__) return;
  window.__VSKLO_PDP_GALLERY_OWNER_V4__ = true;

  var galleryControllers = new Set();
  var desktopLayoutQuery = window.matchMedia('(min-width: 750px)');

  function cleanupOrphanedGalleryControllers() {
    Array.from(galleryControllers).forEach(function (controller) {
      if (!controller || !controller.gallery || !controller.gallery.isConnected) {
        try { controller && controller.cleanup && controller.cleanup(); } catch (error) {}
      }
    });
  }

  function cleanupGalleryControllersInScope(scope) {
    Array.from(galleryControllers).forEach(function (controller) {
      if (!controller || !controller.gallery) return;
      if (scope && scope.contains && scope.contains(controller.gallery)) {
        try { controller.cleanup(); } catch (error) {}
      }
    });
  }

  function refreshRegisteredGalleryLayouts() {
    cleanupOrphanedGalleryControllers();
    Array.from(galleryControllers).forEach(function (controller) {
      if (controller && typeof controller.refreshLayout === 'function') {
        controller.refreshLayout();
      }
    });
  }

  if (desktopLayoutQuery.addEventListener) {
    desktopLayoutQuery.addEventListener('change', refreshRegisteredGalleryLayouts);
  } else if (desktopLayoutQuery.addListener) {
    desktopLayoutQuery.addListener(refreshRegisteredGalleryLayouts);
  }

  function normalizeId(value) {
    var parts = String(value || '').split('/');
    return parts[parts.length - 1];
  }

  function mediaPosition(node) {
    var value = parseInt(node && node.dataset ? node.dataset.vskloMediaPosition : '', 10);
    return isFinite(value) && value > 0 ? value : Number.MAX_SAFE_INTEGER;
  }

  function mergeSharedMedia(baseItems, sharedItems) {
    var merged = baseItems.slice();

    sharedItems.forEach(function (item) {
      var position = mediaPosition(item);
      var index = position === Number.MAX_SAFE_INTEGER
        ? merged.length
        : Math.max(0, Math.min(merged.length, position - 1));
      merged.splice(index, 0, item);
    });

    return merged;
  }

  function buildMediaIndex(source) {
    var byVariant = Object.create(null);
    var shared = [];
    var fallback = [];

    source.forEach(function (item) {
      if (item.hasAttribute('data-vsklo-fallback-media')) fallback.push(item);

      if (item.hasAttribute('data-vsklo-shared-media')) {
        shared.push(item);
        return;
      }

      if (!item.hasAttribute('data-vsklo-variant-id')) return;
      var variantId = normalizeId(item.dataset.vskloVariantId);
      if (!variantId) return;
      if (!byVariant[variantId]) byVariant[variantId] = [];
      byVariant[variantId].push(item);
    });

    shared.sort(function (a, b) {
      return mediaPosition(a) - mediaPosition(b);
    });

    var mergedByVariant = Object.create(null);
    Object.keys(byVariant).forEach(function (variantId) {
      mergedByVariant[variantId] = mergeSharedMedia(byVariant[variantId], shared);
    });

    return {
      mergedByVariant: mergedByVariant,
      fallback: fallback.slice()
    };
  }

  function groupForVariant(index, variantId) {
    var normalized = normalizeId(variantId);
    var group = normalized ? index.mergedByVariant[normalized] : null;
    return group && group.length ? group.slice() : index.fallback.slice();
  }

  function initGallery(gallery) {
    cleanupOrphanedGalleryControllers();
    if (!gallery || gallery.dataset.vskloGalleryOwnerReady === 'true') return;

    var stage = gallery.querySelector('.vsklo-native-look-gallery__stage');
    var allSlides = Array.prototype.slice.call(gallery.querySelectorAll('[data-vsklo-slide]'));
    var allThumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-vsklo-thumb]'));
    var thumbsWrap = gallery.querySelector('.vsklo-native-look-gallery__thumbs-wrap');
    var lightbox = gallery.querySelector('[data-vsklo-lightbox]');
    var productRoot = gallery.closest('.product__item-js');
    var sectionRoot = gallery.closest('[id^="shopify-section-"]');
    var ownerRoot = productRoot || sectionRoot || document;
    var variantPicker = ownerRoot.querySelector('variant-radios-detail, variant-group-detail');

    if (!allSlides.length) {
      gallery.classList.add('is-variant-gallery-ready');
      gallery.dataset.vskloGalleryOwnerReady = 'true';
      return;
    }

    var slideIndex = buildMediaIndex(allSlides);
    var thumbIndex = buildMediaIndex(allThumbs);

    var slides = [];
    var thumbs = [];
    var thumbByMediaId = new Map();
    var current = 0;
    var activeSlide = null;
    var activeThumb = null;
    var galleryTouchStartX = 0;
    var galleryTouchStartY = 0;
    var galleryTouchDeltaX = 0;
    var galleryTouchDeltaY = 0;
    var suppressLightboxUntil = 0;
    var lightboxController = null;
    var lightboxPromise = null;
    var destroyed = false;
    var galleryLoadingTimer = 0;
    var galleryLoadingToken = 0;

    var abortController = typeof AbortController !== 'undefined'
      ? new AbortController()
      : null;
    var manualListeners = [];

    function bind(target, type, handler, options) {
      if (!target || !target.addEventListener) return;
      var finalOptions = options || {};

      if (abortController) {
        if (typeof finalOptions === 'boolean') {
          finalOptions = { capture: finalOptions, signal: abortController.signal };
        } else {
          finalOptions = Object.assign({}, finalOptions, { signal: abortController.signal });
        }
        target.addEventListener(type, handler, finalOptions);
        return;
      }

      target.addEventListener(type, handler, finalOptions);
      manualListeners.push(function () {
        target.removeEventListener(type, handler, finalOptions);
      });
    }

    function isMobileGallery() {
      return !desktopLayoutQuery.matches;
    }

    function clearGalleryLoading(token) {
      if (typeof token === 'number' && token !== galleryLoadingToken) return;
      if (galleryLoadingTimer) {
        window.clearTimeout(galleryLoadingTimer);
        galleryLoadingTimer = 0;
      }
      gallery.classList.remove('is-media-loading');
    }

    function syncGalleryLoading(slide) {
      var token = ++galleryLoadingToken;
      clearGalleryLoading(token);

      if (!slide || String(slide.dataset.vskloMediaType || 'image') !== 'image') return;

      var image = slide.querySelector('img');
      if (!image || (image.complete && image.naturalWidth > 0)) return;

      var showLoading = function () {
        if (
          destroyed ||
          token !== galleryLoadingToken ||
          !gallery.isConnected ||
          (image.complete && image.naturalWidth > 0)
        ) return;
        gallery.classList.add('is-media-loading');
      };

      if (gallery.classList.contains('is-variant-gallery-ready')) {
        galleryLoadingTimer = window.setTimeout(showLoading, 120);
      } else {
        showLoading();
      }

      var finish = function () {
        clearGalleryLoading(token);
      };
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
    }

    function isGalleryVideoTarget(target) {
      return Boolean(
        target &&
        target.closest &&
        target.closest('[data-vsklo-gallery-video-frame]')
      );
    }

    function hydrateExternalVideo(slide) {
      if (!slide) return;
      var frame = slide.querySelector('iframe[data-vsklo-external-video-frame]');
      if (!frame || frame.getAttribute('src')) return;
      var src = frame.getAttribute('data-src');
      if (src) frame.setAttribute('src', src);
    }

    function pauseSlideMedia(slide) {
      if (!slide) return;
      slide.querySelectorAll('video').forEach(function (video) {
        try { video.pause(); } catch (error) {}
      });
    }

    function pauseInactiveVideos() {
      slides.forEach(function (slide, index) {
        if (index !== current) pauseSlideMedia(slide);
      });
    }

    function primeStageImage(index) {
      if (!slides.length) return null;

      var normalizedIndex = (index + slides.length) % slides.length;
      var slide = slides[normalizedIndex];
      if (!slide || String(slide.dataset.vskloMediaType || 'image') !== 'image') return null;

      var image = slide.querySelector('img');
      if (!image) return null;

      // Keep the visible image at high priority and begin fetching only the
      // adjacent image at low priority. This avoids the desktop "blank next
      // slide" delay without making the whole gallery eager.
      image.loading = 'eager';
      if ('fetchPriority' in image) {
        image.fetchPriority = normalizedIndex === current ? 'high' : 'low';
      }

      return image;
    }

    function warmStageImage(index) {
      var image = primeStageImage(index);
      if (!image) return;

      if (!image.complete && typeof image.decode === 'function') {
        var decodePromise = image.decode();
        if (decodePromise && typeof decodePromise.catch === 'function') {
          decodePromise.catch(function () {});
        }
      }
    }

    var adjacentWarmupToken = 0;

    function scheduleAdjacentStageImages(index, includePrevious) {
      var token = ++adjacentWarmupToken;

      // Prime network fetch immediately. Decode remains deferred until the
      // current image has loaded / the browser is idle.
      primeStageImage(index + 1);
      if (includePrevious) primeStageImage(index - 1);

      function run() {
        if (destroyed || token !== adjacentWarmupToken) return;
        warmStageImage(index + 1);
        if (includePrevious) warmStageImage(index - 1);
      }

      var currentSlide = slides[index];
      var currentImage = currentSlide ? currentSlide.querySelector('img') : null;

      if (currentImage && !currentImage.complete) {
        currentImage.addEventListener('load', function () {
          if (token !== adjacentWarmupToken) return;
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(run, { timeout: 700 });
          } else {
            window.setTimeout(run, 120);
          }
        }, { once: true });
        return;
      }

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 700 });
      } else {
        window.setTimeout(run, 120);
      }
    }


    function getSelectedVariantId(preferUrl) {
      var variantInput = ownerRoot.querySelector('form[action*="/cart/add"] [name="id"]');
      var variantIdFromUrl = new URLSearchParams(window.location.search).get('variant');
      var pickerVariantId = variantPicker && variantPicker.currentVariant
        ? variantPicker.currentVariant.id
        : '';
      var inputVariantId = variantInput ? variantInput.value : '';
      var initialVariantId = gallery.dataset.initialVariantId || '';

      return normalizeId(
        preferUrl
          ? variantIdFromUrl || initialVariantId || pickerVariantId || inputVariantId
          : pickerVariantId || inputVariantId || variantIdFromUrl || initialVariantId
      );
    }

    function rebuildThumbMap() {
      thumbByMediaId = new Map();
      thumbs.forEach(function (thumb) {
        var mediaId = String(thumb.dataset.vskloMediaId || '');
        if (mediaId && !thumbByMediaId.has(mediaId)) {
          thumbByMediaId.set(mediaId, thumb);
        }
      });
    }

    function applyGroupVisibility() {
      var visibleSlides = new Set(slides);
      var visibleThumbs = new Set(thumbs);

      allSlides.forEach(function (slide) {
        var visible = visibleSlides.has(slide);
        slide.hidden = !visible;

        if (visible) {
          slide.style.removeProperty('display');
        } else {
          slide.style.setProperty('display', 'none', 'important');
          slide.classList.remove('is-primary', 'is-grid-visible', 'is-extra-media', 'is-hidden');
          pauseSlideMedia(slide);
        }
      });

      allThumbs.forEach(function (thumb) {
        var visible = visibleThumbs.has(thumb);
        thumb.hidden = !visible;
        thumb.classList.remove('is-active');

        if (visible) thumb.style.removeProperty('display');
        else thumb.style.setProperty('display', 'none', 'important');
      });

      if (thumbsWrap) {
        thumbsWrap.classList.remove('vsklo-native-look-gallery__thumbs-wrap--hidden');
      }
    }

    function syncLightboxToCurrent() {
      if (lightboxController && typeof lightboxController.sync === 'function') {
        lightboxController.sync(current);
      }
    }

    function show(index, forceRefresh) {
      if (!slides.length) return;

      var nextIndex = (index + slides.length) % slides.length;
      var nextSlide = slides[nextIndex];
      var nextMediaId = nextSlide ? String(nextSlide.dataset.vskloMediaId || '') : '';
      var nextThumb = nextMediaId ? thumbByMediaId.get(nextMediaId) || null : null;
      var previousSlide = activeSlide;
      var previousThumb = activeThumb;

      current = nextIndex;

      if (forceRefresh || !previousSlide) {
        slides.forEach(function (slide, slideIndex) {
          slide.classList.remove('is-primary', 'is-grid-visible', 'is-extra-media');
          slide.classList.toggle('is-hidden', slideIndex !== current);
        });
        thumbs.forEach(function (thumb) {
          thumb.classList.remove('is-active');
        });
      } else {
        if (previousSlide !== nextSlide) {
          previousSlide.classList.remove('is-primary', 'is-grid-visible', 'is-extra-media');
          previousSlide.classList.add('is-hidden');
        }
        if (nextSlide) {
          nextSlide.classList.remove('is-primary', 'is-grid-visible', 'is-extra-media', 'is-hidden');
        }
        if (previousThumb && previousThumb !== nextThumb) {
          previousThumb.classList.remove('is-active');
        }
      }

      if (nextThumb) nextThumb.classList.add('is-active');

      activeSlide = nextSlide || null;
      activeThumb = nextThumb || null;

      syncGalleryLoading(activeSlide);
      warmStageImage(current);
      scheduleAdjacentStageImages(current, !forceRefresh || current > 0);
      hydrateExternalVideo(activeSlide);

      if (previousSlide && previousSlide !== activeSlide) {
        pauseSlideMedia(previousSlide);
      }
      if (forceRefresh) pauseInactiveVideos();

      syncLightboxToCurrent();
    }

    function scrollToCurrentSlide() {
      var slide = slides[current];
      if (!slide) return;

      requestAnimationFrame(function () {
        if (!slide.isConnected) return;
        var top = slide.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }

    function syncGalleryWithVariant(preferUrl) {
      var selectedVariantId = getSelectedVariantId(preferUrl);
      slides = groupForVariant(slideIndex, selectedVariantId);
      thumbs = groupForVariant(thumbIndex, selectedVariantId);

      if (!slides.length) {
        slides = allSlides.slice();
        thumbs = allThumbs.slice();
      }

      activeSlide = null;
      activeThumb = null;
      current = 0;

      rebuildThumbMap();
      applyGroupVisibility();
      show(0, true);

      gallery.classList.add('is-variant-gallery-ready');
    }

    function ensureLightbox() {
      if (destroyed || !lightbox) return Promise.resolve(null);
      if (lightboxController) return Promise.resolve(lightboxController);
      if (lightboxPromise) return lightboxPromise;

      var src = window.VSKLOPDPGalleryLightboxSrc || '';

      var apiPromise;

      if (
        window.VSKLOPDPGalleryLightbox &&
        typeof window.VSKLOPDPGalleryLightbox.create === 'function'
      ) {
        apiPromise = Promise.resolve(window.VSKLOPDPGalleryLightbox);
      } else if (
        src &&
        window.VSKLOAssetLoader &&
        typeof window.VSKLOAssetLoader.loadScript === 'function'
      ) {
        apiPromise = window.VSKLOAssetLoader.loadScript({
          key: 'pdp-gallery-lightbox',
          src: src,
          parent: 'body',
          ready: function () {
            return window.VSKLOPDPGalleryLightbox || null;
          },
          label: 'VSKLO PDP gallery lightbox'
        });
      } else {
        apiPromise = Promise.reject(new Error('PDP lightbox runtime unavailable'));
      }

      lightboxPromise = apiPromise
        .then(function (api) {
          if (destroyed || !gallery.isConnected || !api || typeof api.create !== 'function') {
            return null;
          }

          if (!lightboxController) {
            lightboxController = api.create({
              gallery: gallery,
              lightbox: lightbox,
              getSlides: function () { return slides; },
              isMobile: isMobileGallery
            });
          }

          return lightboxController;
        })
        .catch(function (error) {
          lightboxPromise = null;
          if (window.VSKLO_DEBUG) {
            console.warn('[VSKLO] PDP gallery lightbox load failed', error);
          }
          return null;
        });

      return lightboxPromise;
    }

    function lightboxIndexForTrigger(trigger) {
      var clickedSlide = trigger && trigger.closest
        ? trigger.closest('[data-vsklo-slide]')
        : null;
      var clickedIndex = slides.indexOf(clickedSlide);
      return clickedIndex >= 0 ? clickedIndex : current;
    }

    function prewarmLightboxFromEvent(event) {
      var target = event && event.target;
      var trigger = target && target.closest
        ? target.closest('[data-vsklo-open-lightbox]')
        : null;
      if (!trigger || !gallery.contains(trigger)) return;
      ensureLightbox();
    }

    function onGalleryClick(event) {
      var target = event && event.target;
      if (!target || !target.closest) return;

      var thumb = target.closest('[data-vsklo-thumb]');
      if (thumb && gallery.contains(thumb)) {
        event.preventDefault();
        var mediaId = String(thumb.dataset.vskloMediaId || '');
        var selectedIndex = slides.findIndex(function (slide) {
          return String(slide.dataset.vskloMediaId || '') === mediaId;
        });
        if (selectedIndex === -1) return;
        show(selectedIndex, false);
        scrollToCurrentSlide();
        return;
      }

      var next = target.closest('[data-vsklo-next]');
      if (next && gallery.contains(next)) {
        event.preventDefault();
        show(current + 1, false);
        return;
      }

      var prev = target.closest('[data-vsklo-prev]');
      if (prev && gallery.contains(prev)) {
        event.preventDefault();
        show(current - 1, false);
        return;
      }

      var opener = target.closest('[data-vsklo-open-lightbox]');
      if (opener && gallery.contains(opener)) {
        if (Date.now() < suppressLightboxUntil) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        var index = lightboxIndexForTrigger(opener);

        ensureLightbox().then(function (controller) {
          if (!controller || destroyed) return;
          controller.open(index);
        });
      }
    }

    function onVariantChange() {
      requestAnimationFrame(function () {
        if (!destroyed && gallery.isConnected) syncGalleryWithVariant(false);
      });
    }

    bind(gallery, 'click', onGalleryClick);
    bind(gallery, 'pointerdown', prewarmLightboxFromEvent, { passive: true });
    bind(gallery, 'focusin', prewarmLightboxFromEvent);

    if (variantPicker) {
      bind(variantPicker, 'change', onVariantChange);
    }

    if (stage) {
      bind(stage, 'touchstart', function (event) {
        if (isGalleryVideoTarget(event.target)) return;
        if (!isMobileGallery()) return;

        var touch = event.changedTouches[0];
        if (!touch) return;

        galleryTouchStartX = touch.clientX;
        galleryTouchStartY = touch.clientY;
        galleryTouchDeltaX = 0;
        galleryTouchDeltaY = 0;
      }, { passive: true });

      bind(stage, 'touchmove', function (event) {
        if (isGalleryVideoTarget(event.target)) return;
        if (!isMobileGallery()) return;

        var touch = event.changedTouches[0];
        if (!touch) return;

        galleryTouchDeltaX = touch.clientX - galleryTouchStartX;
        galleryTouchDeltaY = touch.clientY - galleryTouchStartY;

        if (
          Math.abs(galleryTouchDeltaX) > Math.abs(galleryTouchDeltaY) &&
          Math.abs(galleryTouchDeltaX) > 12 &&
          event.cancelable
        ) {
          event.preventDefault();
        }
      }, { passive: false });

      bind(stage, 'touchend', function (event) {
        if (isGalleryVideoTarget(event.target)) return;
        if (!isMobileGallery()) return;

        if (
          Math.abs(galleryTouchDeltaX) > 45 &&
          Math.abs(galleryTouchDeltaX) > Math.abs(galleryTouchDeltaY)
        ) {
          suppressLightboxUntil = Date.now() + 450;
          show(galleryTouchDeltaX < 0 ? current + 1 : current - 1, false);
        }
      }, { passive: true });
    }

    function refreshLayout() {
      if (!slides.length || !gallery.isConnected) return;
      show(Math.min(Math.max(current, 0), slides.length - 1), true);
    }

    var controller = {
      gallery: gallery,
      refreshLayout: refreshLayout,
      closeLightbox: function () {
        if (
          lightboxController &&
          typeof lightboxController.isOpen === 'function' &&
          lightboxController.isOpen() &&
          typeof lightboxController.close === 'function'
        ) {
          lightboxController.close();
        }
      },
      cleanup: null
    };

    controller.cleanup = function () {
      if (!galleryControllers.has(controller)) return;
      galleryControllers.delete(controller);
      destroyed = true;

      if (abortController) {
        try { abortController.abort(); } catch (error) {}
      } else {
        manualListeners.forEach(function (cleanup) {
          try { cleanup(); } catch (error) {}
        });
        manualListeners.length = 0;
      }

      if (lightboxController && typeof lightboxController.destroy === 'function') {
        try { lightboxController.destroy(); } catch (error) {}
      } else if (window.VSKLOOverlayState) {
        window.VSKLOOverlayState.release('pdp-lightbox');
      }

      ++galleryLoadingToken;
      clearGalleryLoading(galleryLoadingToken);
      gallery.removeAttribute('data-vsklo-gallery-owner-ready');
      gallery.__vskloGalleryController = null;
      lightboxController = null;
      lightboxPromise = null;
    };

    gallery.__vskloGalleryController = controller;
    galleryControllers.add(controller);

    syncGalleryWithVariant(true);
    gallery.dataset.vskloGalleryOwnerReady = 'true';
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    if (root.nodeType === 1 && root.matches && root.matches('[data-vsklo-gallery]')) {
      initGallery(root);
    }
    root.querySelectorAll('[data-vsklo-gallery]').forEach(initGallery);
  }

  window.VSKLOInitPDPGallery = init;

  document.addEventListener('shopify:section:load', function (event) {
    init(event && event.target ? event.target : document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    cleanupGalleryControllersInScope(event && event.target ? event.target : null);
  });

  document.addEventListener('vsklo:content-updated', function (event) {
    cleanupOrphanedGalleryControllers();

    var scope =
      event &&
      event.detail &&
      event.detail.scope &&
      event.detail.scope.querySelectorAll
        ? event.detail.scope
        : document;

    init(scope);
  });

  window.addEventListener('pageshow', function (event) {
    cleanupOrphanedGalleryControllers();

    if (!event || !event.persisted) return;

    Array.from(galleryControllers).forEach(function (controller) {
      if (controller && typeof controller.closeLightbox === 'function') {
        try { controller.closeLightbox(); } catch (error) {}
      }
    });

    document.documentElement.classList.remove('vsklo-product-lightbox-open');
    document.body && document.body.classList.remove('vsklo-product-lightbox-open');
    if (window.VSKLOOverlayState) window.VSKLOOverlayState.release('pdp-lightbox');
  });

  // Safety fallback: if PDP markup was replaced without a lifecycle event,
  // bind during pointerdown so the following click still reaches the owner.
  document.addEventListener('pointerdown', function (event) {
    var target = event && event.target;
    var gallery = target && target.closest
      ? target.closest('[data-vsklo-gallery]')
      : null;

    if (gallery && gallery.dataset.vskloGalleryOwnerReady !== 'true') {
      initGallery(gallery);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    }, { once: true });
  } else {
    init(document);
  }
})();